import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  getCurrentUser,
  signInWithRedirect,
  signOut as amplifySignOut,
  fetchAuthSession
} from 'aws-amplify/auth';

interface User {
  username: string;
  email: string;
  emailVerified: boolean;
  mfaEnabled?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      // Check if we're returning from OAuth callback
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      // Fetch the current session (handles OAuth code exchange automatically)
      const session = await fetchAuthSession();

      // If we have a valid session, get user details
      if (session.tokens) {
        const currentUser = await getCurrentUser();

        setUser({
          username: currentUser.username,
          email: currentUser.signInDetails?.loginId || '',
          emailVerified: true,
          mfaEnabled: false
        });

        // Clean up URL if we came from OAuth callback
        if (code) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  };

  const handleSignIn = async () => {
    try {
      await signInWithRedirect();
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading: loading || !authChecked,
    signIn: handleSignIn,
    signOut: handleSignOut,
    isAuthenticated: !!user && authChecked
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
